import { Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimatedHeadline from "@/components/AnimatedHeadline";
import { PHONE_HREF, WHATSAPP_HREF } from "@/lib/contact";

export default function CtaSection() {
  const sectionRef   = useRef(null);
  const circle1Ref   = useRef(null);
  const circle2Ref   = useRef(null);
  const labelRef     = useRef(null);
  const titleLine1Ref = useRef(null);
  const titleLine2Ref = useRef(null);
  const goldLineRef  = useRef(null);
  const bodyTextRef  = useRef(null);
  const buttonsRef   = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Background circles parallax
      gsap.to(circle1Ref.current, {
        y: -70,
        ease: "none",
        scrollTrigger: { trigger: sectionRef.current, start: "top bottom", end: "bottom top", scrub: true },
      });
      gsap.to(circle2Ref.current, {
        y: 50,
        ease: "none",
        scrollTrigger: { trigger: sectionRef.current, start: "top bottom", end: "bottom top", scrub: true },
      });

      // Label
      gsap.fromTo(labelRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
          scrollTrigger: { trigger: labelRef.current, start: "top 82%" } });

      // Title lines with blur reveal
      gsap.fromTo(titleLine1Ref.current,
        { y: "115%", rotationX: -75, filter: "blur(6px)" },
        { y: "0%", rotationX: 0, filter: "blur(0px)", duration: 0.95, ease: "power3.out",
          scrollTrigger: { trigger: titleLine1Ref.current, start: "top 88%" } });

      gsap.fromTo(titleLine2Ref.current,
        { y: "115%", rotationX: -75, filter: "blur(6px)" },
        { y: "0%", rotationX: 0, filter: "blur(0px)", duration: 0.95, ease: "power3.out",
          scrollTrigger: { trigger: titleLine2Ref.current, start: "top 92%" } });

      // Gold line draw
      gsap.fromTo(goldLineRef.current,
        { scaleX: 0, transformOrigin: "center center" },
        { scaleX: 1, duration: 0.8, ease: "power2.out",
          scrollTrigger: { trigger: goldLineRef.current, start: "top 85%" } });

      // Body text
      gsap.fromTo(bodyTextRef.current,
        { opacity: 0, y: 25 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out",
          scrollTrigger: { trigger: bodyTextRef.current, start: "top 88%" } });

      // Buttons stagger
      if (buttonsRef.current) {
        gsap.fromTo(Array.from(buttonsRef.current.children),
          { opacity: 0, y: 20, scale: 0.95 },
          {
            opacity: 1, y: 0, scale: 1,
            stagger: 0.15, duration: 0.6, ease: "power3.out",
            scrollTrigger: { trigger: buttonsRef.current, start: "top 85%" },
          });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="cta-section"
      className="relative py-28 md:py-36 bg-[#1a1a1a] overflow-hidden">

      {/* Parallax decorative circles */}
      <div className="absolute inset-0 pointer-events-none">
        <div ref={circle1Ref} className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-[#c9a96e]/[0.04]" />
        <div ref={circle2Ref} className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full border border-[#c9a96e]/[0.03]" />
      </div>

      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9a96e]/30 to-transparent" />

      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
        <div>
          <span ref={labelRef} className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif", opacity: 0 }}>
            Estamos aquí para ayudarte
          </span>
          <h2
            data-testid="cta-title"
            className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 mb-7 text-[#f8f6f3] leading-[1.15]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <div className="overflow-hidden">
              <div ref={titleLine1Ref}>¿Tienes dudas?</div>
            </div>
            <div className="overflow-hidden">
              <div ref={titleLine2Ref} className="italic text-[#c9a96e]">Contacta sin compromiso</div>
            </div>
          </h2>
          <div ref={goldLineRef} className="w-12 h-[2px] bg-[#c9a96e]/50 mx-auto mb-7" style={{ transformOrigin: "center center" }} />
          <p ref={bodyTextRef} className="text-[15px] text-[#f8f6f3]/40 font-light leading-[1.8] max-w-xl mx-auto mb-12" style={{ fontFamily: "'Manrope', sans-serif", opacity: 0 }}>
            Nuestro equipo está disponible para resolver cualquier consulta.
            Llámanos o escríbenos por WhatsApp y te atenderemos de forma inmediata.
          </p>
        </div>

        <div ref={buttonsRef} className="flex flex-col sm:flex-row justify-center gap-4">
          <a href={PHONE_HREF} data-testid="cta-call-btn" style={{ opacity: 0 }}>
            <Button
              size="lg"
              className="bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm uppercase tracking-[0.2em] text-[11px] font-semibold h-14 px-10 transition-all duration-300 shadow-lg shadow-[#c0392b]/20 w-full sm:w-auto">
              <Phone className="w-4 h-4 mr-2.5" />
              Llamar ahora
            </Button>
          </a>
          <a
            href={WHATSAPP_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-btn"
            style={{ opacity: 0 }}>
            <Button
              size="lg"
              className="bg-[#c9a96e]/10 border border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#1a1a1a] rounded-sm uppercase tracking-[0.2em] text-[11px] font-semibold h-14 px-10 transition-all duration-300 w-full sm:w-auto">
              <MessageCircle className="w-4 h-4 mr-2.5" />
              Escribir por WhatsApp
            </Button>
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9a96e]/30 to-transparent" />
    </section>
  );
}
